import { Box } from '@wix/design-system';
import { SparklesFilledSmall } from '@wix/wix-ui-icons-common';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { convertElementStatusToBooleanStatus, ElementStatus, RuleElement, RuleElementListHeader } from '../../..';
import { decodeHtmlEntities, makeHeadingElementKey } from '../../../utils/heading-structure-utils';
import { AiNotification } from './components/ai-flow/ai-notification';
import { useOptionalHeadingStructureAIContext } from './components/ai-flow/context';
import { FixWithAiButton } from './components/ai-flow/fix-with-ai-button';
import { HeadingStructureRuleGenericContent } from './components/generic/heading-structure-rule-generic-content';
import { HeadingStructureRuleRegularContent } from './components/regular/heading-structure-rule-regular-content';
import { HeadingStructureRuleHeader } from './heading-structure-rule-header';
import { useElementCallbacks } from './hooks';
import { resolveDisplayedStatus } from './resolve-displayed-status';
import { useAiNotificationCounts } from './use-ai-notification-counts';
import { useHeadingStatusPreview } from './use-heading-status-preview';
export const HeadingStructureRuleFixer = _ref => {
  let {
    headings,
    texts,
    events,
    showLoader = false,
    selectedElementId,
    title,
    timelineTransComponents,
    ruleChecker,
    iconButtonPriority,
    iconButtonSkin,
    notHoveredBorderColor,
    hoveredBorderColor,
    width: ruleElementWidth = 'full'
  } = _ref;
  const aiContext = useOptionalHeadingStructureAIContext();
  const isAILoading = (aiContext == null ? void 0 : aiContext.state.isAILoading) ?? false;
  const aiResult = (aiContext == null ? void 0 : aiContext.state.aiResult) ?? null;
  const showFixWithAiButtonEperiment = (aiContext == null ? void 0 : aiContext.showFixWithAiButton) ?? false;
  const previewStatusById = useHeadingStatusPreview(headings, aiResult, ruleChecker);
  const aiSuggestedKeys = useMemo(() => {
    var _aiResult$suggestions;
    return new Set((aiResult == null || (_aiResult$suggestions = aiResult.suggestions) == null ? void 0 : _aiResult$suggestions.filter(s => s.proposedTag).map(s => makeHeadingElementKey(s.id, s.attributeId, s.uiElementId))) ?? []);
  }, [aiResult]);
  const getDisplayedStatus = useCallback(heading => resolveDisplayedStatus(heading, aiSuggestedKeys, previewStatusById), [aiSuggestedKeys, previewStatusById]);
  const completedSize = useMemo(() => headings.filter(heading => convertElementStatusToBooleanStatus(getDisplayedStatus(heading))).length, [headings, getDisplayedStatus]);
  const total = headings.length;
  const {
    fixedCount,
    aiManualCount,
    aiSuggestedElementIds,
    firstFailedGenericSuggestionElementId
  } = useAiNotificationCounts(headings, aiResult);

  // Unified event handler that calls the consumer's handler with appropriate action
  const handleElementEvent = useCallback((action, elementId, componentId, elementIndex, elementStatus, additionalParams) => {
    return events.onElementEvent({
      action,
      componentId,
      elementId,
      elementIndex,
      elementStatus,
      ...additionalParams
    });
  }, [events]);
  const handleClick = useCallback((elementId, elementIndex, elementStatus, componentId) => () => {
    // First, fire the click event
    handleElementEvent('click', elementId, componentId, elementIndex, elementStatus, {
      ctaName: 'Edit'
    });

    // Then handle the toggle logic
    const isCurrentlyOpen = selectedElementId === elementId;
    // Fire the toggle event
    handleElementEvent('toggle', elementId, componentId, elementIndex, elementStatus, {
      isOpen: !isCurrentlyOpen
    });
  }, [handleElementEvent, selectedElementId]);
  const handleStatusChange = useCallback((elementId, componentId, elementIndex, elementStatus, headingDefinition) => () => {
    handleElementEvent('statusChange', elementId, componentId, elementIndex, elementStatus, {
      value: headingDefinition
    });
  }, [handleElementEvent]);
  const elementCallbacks = useElementCallbacks(headings, handleElementEvent);
  const elementRefs = useRef(new Map());
  const handleShowMore = useCallback(() => {
    var _elementRefs$current$;
    aiContext == null || aiContext.actions.onShowMore == null || aiContext.actions.onShowMore();
    const targetId = firstFailedGenericSuggestionElementId;
    if (!targetId) {
      return;
    }
    (_elementRefs$current$ = elementRefs.current.get(targetId)) == null || _elementRefs$current$.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
    if (targetId !== selectedElementId) {
      const headingIndex = headings.findIndex(h => h.elementId === targetId);
      const heading = headings[headingIndex];
      if (heading) {
        events.onElementEvent({
          action: 'toggle',
          componentId: heading.componentId,
          elementId: targetId,
          elementIndex: headingIndex,
          elementStatus: heading.status,
          isOpen: true
        });
      }
    }
  }, [aiContext, firstFailedGenericSuggestionElementId, selectedElementId, headings, events]);
  const hasAppliedAiFixes = (aiContext == null ? void 0 : aiContext.state.hasAppliedAiFixes) ?? false;
  const showFixedRow = fixedCount > 0 && (!hasAppliedAiFixes || aiManualCount === 0);
  const showAiNotification = (showFixedRow || aiManualCount > 0) && aiResult !== null;
  const showFixWithAiButton = !showAiNotification;

  // Report a notification-view on first appearance and whenever the payload
  // (suggestion counts or applied state) changes as the user edits headings.
  // Depend ONLY on the payload values — NOT on onAiNotificationView. That
  // callback's identity churns with biLogger (applying a fix re-verifies the
  // rule and updates the task-info atom), which would otherwise re-fire the
  // event with an unchanged payload.
  const onAiNotificationView = aiContext == null ? void 0 : aiContext.actions.onAiNotificationView;
  useEffect(() => {
    if (!showAiNotification || !onAiNotificationView) {
      return;
    }
    onAiNotificationView({
      genericCount: aiManualCount,
      isApplied: hasAppliedAiFixes,
      nonGenericCount: fixedCount
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAiNotification, fixedCount, aiManualCount, hasAppliedAiFixes]);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2",
    width: "100%"
  }, showFixWithAiButtonEperiment && aiContext && showFixWithAiButton && /*#__PURE__*/React.createElement(Box, {
    align: "left",
    padding: "SP1 0 SP2 0"
  }, /*#__PURE__*/React.createElement(FixWithAiButton, {
    disabled: completedSize === total,
    isLoading: isAILoading,
    texts: texts.ai ? {
      buttonLabel: texts.ai.fixWithAiButtonLabel,
      cancelLabel: texts.ai.fixWithAiCancelLabel,
      disabledTooltip: texts.ai.fixWithAiButtonTooltip
    } : undefined,
    onCancel: aiContext.actions.onCancel,
    onClick: aiContext.actions.onAIFix
  })), showAiNotification && /*#__PURE__*/React.createElement(Box, {
    padding: "SP1 0 SP2 0"
  }, /*#__PURE__*/React.createElement(AiNotification, {
    fixedCount: fixedCount,
    hasAppliedAiFixes: aiContext.state.hasAppliedAiFixes,
    manualCount: aiManualCount,
    texts: texts.ai ? {
      aiMistakesText: texts.ai.aiMistakesText,
      appliedTitle: texts.ai.appliedTitle,
      applyLabel: texts.ai.applyLabel,
      fixedTitle: texts.ai.fixedTitle,
      manualTitle: texts.ai.manualTitle,
      showMoreLabel: texts.ai.showMoreLabel,
      undoTooltip: texts.ai.undoTooltip
    } : undefined,
    onApplyAiFixes: aiContext.actions.onApplyAiFixes,
    onShowMore: handleShowMore,
    onUndo: aiContext.actions.onUndo
  })), title && /*#__PURE__*/React.createElement(RuleElementListHeader, {
    completedSize: completedSize,
    title: title,
    totalSize: total
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2"
  }, headings.map((heading, index) => {
    const displayedStatus = getDisplayedStatus(heading);
    const isOpen = selectedElementId === heading.elementId;
    const isGeneric = heading.isGeneric || false;
    const isAiSuggestedIcon = isGeneric && aiSuggestedElementIds.has(makeHeadingElementKey(heading.componentId, heading.attributeId, heading.elementId));
    const showSparkle = showFixWithAiButtonEperiment && isAiSuggestedIcon;
    const shouldSuppressFailedReason = displayedStatus === ElementStatus.Generated || isAiSuggestedIcon && texts.timeline.generatedState !== undefined;
    const effectiveFailedReason = shouldSuppressFailedReason ? undefined : heading.failedReason;
    return /*#__PURE__*/React.createElement("div", {
      key: heading.elementId,
      ref: el => {
        if (el) {
          elementRefs.current.set(heading.elementId, el);
        } else {
          elementRefs.current.delete(heading.elementId);
        }
      }
    }, /*#__PURE__*/React.createElement(RuleElement, {
      contentComponent: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Box, {
        dataHook: isGeneric ? 'generic-content' : 'regular-content',
        direction: "vertical",
        gap: "SP3",
        padding: "0 SP1"
      }, isGeneric ? /*#__PURE__*/React.createElement(HeadingStructureRuleGenericContent, {
        aiProposedDefinition: heading.aiProposedDefinition,
        callbacks: elementCallbacks,
        elementIndex: index,
        failedReason: effectiveFailedReason,
        headingDefinition: heading.headingDefinition ?? 'p',
        isAiSuggested: isAiSuggestedIcon,
        status: displayedStatus,
        texts: texts,
        timelineTransComponents: timelineTransComponents
      }) : /*#__PURE__*/React.createElement(HeadingStructureRuleRegularContent, {
        callbacks: elementCallbacks,
        dataHook: "regular",
        elementIndex: index,
        failedReason: effectiveFailedReason,
        headingDefinition: heading.headingDefinition ?? 'p',
        showLoader: showLoader,
        status: displayedStatus,
        texts: texts
      }))),
      disabled: isAILoading,
      headerComponent: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(HeadingStructureRuleHeader, {
        headingContent: decodeHtmlEntities(heading.content),
        headingDefinition: heading.headingDefinition,
        isLoading: isAILoading,
        status: convertElementStatusToBooleanStatus(displayedStatus)
      })),
      headerSuffix: showSparkle ? /*#__PURE__*/React.createElement(Box, {
        color: "B10"
      }, /*#__PURE__*/React.createElement(SparklesFilledSmall, null)) : undefined,
      hoveredBorderColor: hoveredBorderColor,
      iconButtonAriaLabel: texts.iconButtonAriaLabel,
      iconButtonPriority: iconButtonPriority,
      iconButtonSkin: iconButtonSkin,
      iconButtonToolTip: texts.iconButtonToolTip,
      id: heading.elementId,
      isLoading: isAILoading,
      isOpen: isOpen,
      notHoveredBorderColor: notHoveredBorderColor,
      showIconButton: !showSparkle,
      status: displayedStatus,
      width: ruleElementWidth,
      onClick: handleClick(heading.elementId, index, displayedStatus, heading.componentId),
      onStatusChange: handleStatusChange(heading.elementId, heading.componentId, index, displayedStatus, heading.headingDefinition)
    }));
  })));
};
//# sourceMappingURL=heading-structure-rule-fixer.js.map